import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Gift, Sparkles } from "lucide-react";
import toast from "react-hot-toast";
import { newsletterApi } from "@/lib/api";

const STORAGE_KEY = "aarsa_first_visit_dialog_shown";

export default function FirstTimeVisitorDialog() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Don't show dialog on admin pages
    const isAdminPage = location.pathname.startsWith("/admin") || location.pathname.startsWith("/dashboard");
    if (isAdminPage) {
      return;
    }

    // Check if user has seen the dialog before
    const hasSeenDialog = localStorage.getItem(STORAGE_KEY);
    if (!hasSeenDialog) {
      // Show dialog after a short delay for better UX
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Validate email format
  const isValidEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleClaimDiscount = async () => {
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    if (!isValidEmail(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);

    try {
      try {
        await newsletterApi.subscribe(email.trim());
        toast.success("🎉 Discount claimed! Check your email for your discount code.");
      } catch (error: any) {
        // Even if newsletter subscription fails, still claim discount
        const errorMessage = error?.response?.data?.detail || error?.message;
        if (errorMessage?.includes("already subscribed")) {
          toast.success("🎉 Discount claimed! You're already subscribed to our newsletter.");
        } else {
          toast.success("🎉 Discount claimed! Check your email for your discount code.");
        }
      }

      // Mark dialog as shown
      localStorage.setItem(STORAGE_KEY, "true");
      setIsOpen(false);
    } catch (error: any) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNoThanks = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={handleNoThanks}
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
              {/* Close Button */}
              <button
                onClick={handleNoThanks}
                className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>

              {/* Header with Gradient */}
              <div className="relative bg-gradient-to-br from-aarsa-gold-400 via-aarsa-gold-500 to-aarsa-sage-500 p-8 text-center overflow-hidden">
                <div 
                  className="absolute inset-0 opacity-20"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                  }}
                ></div>
                
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring" }}
                  className="relative z-10"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full mb-4">
                    <Gift className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-2">
                    Welcome to Aarsa!
                  </h2>
                  <p className="text-white/90 text-lg font-medium">
                    Get ₹100 OFF on your order
                  </p>
                </motion.div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-gray-700 text-center mb-6">
                  Sign up for instant discount and unlock exclusive offers!
                </p>

                {/* Input Field */}
                <div className="relative mb-4">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10">
                    <Mail className={`w-5 h-5 ${isValidEmail(email) ? "text-aarsa-sage-600" : "text-gray-400"}`} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent transition-all"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleClaimDiscount}
                    disabled={isSubmitting || !email.trim() || !isValidEmail(email)}
                    className="flex-1 bg-gradient-to-r from-aarsa-sage-600 to-aarsa-sage-700 text-white py-4 px-6 rounded-xl font-semibold hover:from-aarsa-sage-700 hover:to-aarsa-sage-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl relative overflow-hidden group"
                  >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Claiming...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-5 h-5" />
                          Claim Discount
                        </>
                      )}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  </button>

                  <button
                    onClick={handleNoThanks}
                    disabled={isSubmitting}
                    className="flex-1 sm:flex-initial px-6 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    No Thanks
                  </button>
                </div>

                {/* Discount Code Display */}
                <div className="mt-6 p-4 bg-gradient-to-r from-aarsa-gold-50 to-aarsa-sage-50 rounded-xl border-2 border-dashed border-aarsa-gold-300 text-center">
                  <p className="text-sm text-gray-600 mb-1">Your discount code:</p>
                  <p className="text-2xl font-bold text-aarsa-gold-700 tracking-wider">
                    WELCOME100
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

